<template>
  <div class="branding-copy" :style="branding_preview_style">
    <div class="branding-copy__intro">
      <div><div class="branding-copy__eyebrow">Brand system</div>
      <h2>Branding &amp; copy</h2>
      <p>Shape the interface your team uses every day with your colours, logo and welcome message.</p>
    </div>
    <span class="branding-copy__status" :class="{ saved: saved }">
      <i class="bi" :class="saved ? 'bi-check-circle-fill' : 'bi-eye-fill'"></i>{{ saved ? 'Preview saved' : 'Live preview' }}</span>
    </div>
    <div class="row g-4">
      <div class="col-12 col-xl-7">
        <div class="branding-copy__card">
          <div class="branding-copy__card-heading">
            <h3>Visual identity</h3>
            <p>Choose the palette used for navigation, actions and highlights.</p>
          </div><div class="row g-3">
            <div v-for="field in color_fields" :key="field.key" class="col-12 col-md-6">
              <label :for="`branding-${field.key}`">{{ field.label }}</label>
              <div class="branding-copy__color"><input :id="`branding-${field.key}`" v-model="form.colors[field.key]" type="color" @input="apply_preview">
                <input v-model="form.colors[field.key]" type="text" maxlength="20" @input="apply_preview">
              </div>
            </div>
          </div>
          <hr>
          <label for="branding-logo">Organisation logo</label>
          <div class="branding-copy__upload">
            <span>
              <i class="bi bi-cloud-arrow-up"></i> PNG, JPG, SVG or WebP up to 1.5 MB</span>
              <input id="branding-logo" type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" @change="read_logo">
            </div>
            <div v-if="form.logo" class="branding-copy__selected">
              <img :src="form.logo" alt="Logo preview">
              <button type="button" class="btn btn-link" @click="form.logo = ''">Remove</button>
            </div>
          </div>
        <div class="branding-copy__card mt-4"><div class="branding-copy__card-heading">
          <h3>Interface copy</h3>
          <p>Set the words shown in your workspace header and welcome areas.</p>
        </div>
        <label for="branding-name">Workspace name</label>
        <input id="branding-name" v-model="form.copy.workspace_name" class="form-control" maxlength="120" placeholder="e.g. Acme Holdings" @input="apply_preview">
        <label for="branding-message" class="mt-3">Welcome message</label>
        <textarea id="branding-message" v-model="form.copy.welcome_message" class="form-control" rows="3" maxlength="300" placeholder="A short message for your team" @input="apply_preview"></textarea>
      </div>
      </div>
      <div class="col-12 col-xl-5"><div class="branding-copy__preview">
        <div class="branding-copy__preview-head">
          <img v-if="form.logo" :src="form.logo" alt="" class="branding-copy__logo">
          <div v-else class="branding-copy__mark">V</div>
          <small>Workspace preview</small>
          <h3>{{ form.copy.workspace_name || 'Your organisation' }}</h3>
          <p>{{ form.copy.welcome_message || 'Welcome to your workspace.' }}</p>
        </div>
        <div class="branding-copy__preview-body">
          <div class="d-flex justify-content-between mb-4"><strong>Dashboard</strong>
            <button type="button" class="btn">New case</button>
          </div>
          <div class="branding-copy__stats">
            <span></span><span></span><span></span>
          </div>
          <div class="branding-copy__line">
          </div>
          <div class="branding-copy__line short"></div>
        </div>
      </div>
      <button type="button" class="btn branding-copy__save w-100 mt-3" @click="apply_branding"><i class="bi bi-check2 me-2"></i>{{ saved ? 'Branding applied' : 'Apply Branding' }}</button>
      <p class="branding-copy__note">Changes remain in preview until you apply the branding.</p>
    </div>
    </div>
  </div>
</template>

<script>
function defaults() {
  return {
    colors: {
      primary: '#276c58',
      secondary: '#173f3a',
      accent: '#d7a94b',
      text: '#27332f',
    },
    logo: '',
    copy: {
      workspace_name: '',
      welcome_message: '',
    },
  };
}

export default {
  name: 'BrandingCopy',

  data: () => ({
    form: defaults(),
    saved: false,
    color_fields: [
      { key: 'primary', label: 'Primary colour' },
      { key: 'secondary', label: 'Secondary colour' },
      { key: 'accent', label: 'Accent colour' },
      { key: 'text', label: 'Text colour' },
    ],
  }),

  computed: {
    branding_preview_style() {
      return Object.fromEntries(
        Object.entries(this.form.colors).map(([key, value]) => [
          `--${key}-color`,
          value,
        ])
      );
    },
  },

  mounted() {
    try {
      const saved = JSON.parse(
        localStorage.getItem('verdict_tenant_branding_preview') || 'null'
      );

      if (saved) {
        this.form = {
          ...defaults(),
          ...saved,
          colors: {
            ...defaults().colors,
            ...(saved.colors || {}),
          },
          copy: {
            ...defaults().copy,
            ...(saved.copy || {}),
          },
        };
      }
    } catch {
      // Ignore malformed preview data.
    }

    this.apply_preview();
  },

  methods: {
    apply_preview() {
      this.saved = false;
    },

    read_logo(event) {
      const file = event.target.files?.[0];

      if (!file) return;

      if (file.size > 1500000) {
        window.alert('Please choose a logo smaller than 1.5 MB.');
        event.target.value = '';
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        this.form.logo = reader.result;
        this.saved = false;
      };

      reader.readAsDataURL(file);
    },

    apply_branding() {
      Object.entries(this.form.colors).forEach(([key, value]) => {
        if (value) {
          document.documentElement.style.setProperty(
            `--${key}-color`,
            value
          );
        }
      });

      localStorage.setItem(
        'verdict_tenant_branding_preview',
        JSON.stringify(this.form)
      );

      this.saved = true;
    },
  },
};
</script>

<style scoped>
.branding-copy {
  padding: 1.25rem 0 2rem;
  color: var(--theme-ink-strong);
}

.branding-copy__intro {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.branding-copy__eyebrow,
.branding-copy label {
  color: var(--theme-ink-muted);
  font: 600 0.68rem Poppins, sans-serif;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.branding-copy__intro h2 {
  margin: 0.35rem 0 0.4rem;
  font-size: 1.45rem;
}

.branding-copy__intro p,
.branding-copy__card-heading p,
.branding-copy__note {
  color: var(--theme-ink-muted);
  font-size: 0.8rem;
  line-height: 1.5;
}

.branding-copy__status {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 0.35rem;
  padding: 0.45rem 0.7rem;
  border: 1px solid var(--theme-border-soft);
  border-radius: 999px;
  color: var(--theme-ink-muted);
  font-size: 0.7rem;
  line-height: 1.2;
  white-space: nowrap;
}

.branding-copy__status.saved {
  color: var(--primary-color);
}

.branding-copy__card,
.branding-copy__preview {
  border: 1px solid var(--theme-border-soft);
  border-radius: 0.45rem;
  background: #fff;
  box-shadow: 0 0.4rem 1.2rem rgba(38, 48, 44, 0.04);
}

.branding-copy__card {
  padding: 1.35rem 1.5rem 1.5rem;
}

.branding-copy__card-heading {
  margin-bottom: 1.2rem;
}

.branding-copy__card-heading h3 {
  margin: 0 0 0.35rem;
  font-size: 1rem;
}

.branding-copy__card-heading p {
  margin: 0;
}

.branding-copy hr {
  margin: 1.4rem 0;
  border-color: var(--theme-border-soft);
}

.branding-copy__color {
  display: flex;
  overflow: hidden;
  margin-top: 0.5rem;
  border: 1px solid var(--theme-border-soft);
  border-radius: 0.3rem;
}

.branding-copy__color input[type='color'] {
  width: 2.5rem;
  height: 2.35rem;
  padding: 0.2rem;
  border: 0;
}

.branding-copy__color input[type='text'] {
  min-width: 0;
  flex: 1;
  padding: 0.55rem 0.65rem;
  border: 0;
  font-size: 0.78rem;
}

.branding-copy__upload {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  min-height: 4.25rem;
  margin-top: 0.5rem;
  padding: 0.8rem 1rem;
  border: 1px dashed #b8c7bf;
  border-radius: 0.35rem;
  background: #fbfcfa;
  color: var(--theme-ink-muted);
  font-size: 0.75rem;
}

.branding-copy__upload i {
  margin-right: 0.4rem;
  color: var(--primary-color);
  font-size: 1.35rem;
}

.branding-copy__upload input {
  max-width: 8rem;
  font-size: 0.68rem;
}

.branding-copy__selected {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  padding: 0.65rem 0.8rem;
  background: var(--theme-surface-base);
}

.branding-copy__selected img {
  max-width: 12rem;
  max-height: 3rem;
}

.branding-copy__selected button {
  padding: 0;
  color: #a33b1f;
  font-size: 0.72rem;
}

.branding-copy .form-control {
  border-color: var(--theme-border-soft);
  border-radius: 0.3rem;
  font-size: 0.8rem;
}

.branding-copy__preview {
  overflow: hidden;
}

.branding-copy__preview-head {
  min-height: 13.5rem;
  padding: 1.5rem;
  background: var(--secondary-color);
  color: #fff;
}

.branding-copy__mark {
  display: grid;
  width: 2.6rem;
  height: 2.6rem;
  margin-bottom: 1rem;
  place-items: center;
  border-radius: 0.65rem;
  background: var(--primary-color);
  font-weight: 700;
}

.branding-copy__logo {
  display: block;
  max-width: 11rem;
  max-height: 3rem;
  margin-bottom: 1rem;
  padding: 0.3rem;
  border-radius: 0.25rem;
  background: #fff;
}

.branding-copy__preview-head small {
  display: block;
  margin-bottom: 0.55rem;
  color: #ffffffad;
  font-size: 0.63rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.branding-copy__preview-head h3 {
  font-size: 1.15rem;
}

.branding-copy__preview-head p {
  color: #ffffffbf;
  font-size: 0.78rem;
}

.branding-copy__preview-body {
  padding: 1.35rem 1.5rem 1.5rem;
}

.branding-copy__preview-body .btn,
.branding-copy__save {
  border: 0;
  background: var(--primary-color);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
}

.branding-copy__stats {
  display: flex;
  gap: 0.45rem;
  margin-bottom: 1.3rem;
}

.branding-copy__stats span {
  width: 30%;
  height: 2.8rem;
  border-radius: 0.3rem;
  background: color-mix(in srgb, var(--primary-color) 12%, white);
}

.branding-copy__line {
  height: 0.6rem;
  margin-bottom: 0.7rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--primary-color) 14%, white);
}

.branding-copy__line.short {
  width: 65%;
}

.branding-copy__note {
  margin: 0.5rem 0 0;
}

@media (max-width: 575px) {
  .branding-copy__intro,
  .branding-copy__upload {
    align-items: flex-start;
    flex-direction: column;
  }

  .branding-copy__upload input {
    max-width: 100%;
  }
}
</style>
